import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataLoader } from '@/components/ui/DataLoader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  useMyChangeRequests,
  useCancelChangeRequest,
} from '@/hooks/react-query/useChangeRequests';
import { formatDate } from '@/utils/formatters';
import type { PortalChangeRequestList } from '@/types/changeRequest';
import { CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';

export function MyChangeRequestsPage() {
  const { data: response, isLoading, error } = useMyChangeRequests();
  const cancelMutation = useCancelChangeRequest();

  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const handleCancel = async (requestId: string) => {
    if (!confirm('Are you sure you want to cancel this change data?')) {
      return;
    }

    setCancellingId(requestId);
    try {
      await cancelMutation.mutateAsync(requestId);
    } catch (error) {
      console.error('Failed to cancel request:', error);
    } finally {
      setCancellingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      {
        icon: React.ReactNode;
        variant: 'default' | 'secondary' | 'destructive' | 'outline';
      }
    > = {
      pending: {
        icon: <Clock className="mr-1 h-3 w-3" />,
        variant: 'outline',
      },
      approved: {
        icon: <CheckCircle className="mr-1 h-3 w-3" />,
        variant: 'default',
      },
      rejected: {
        icon: <XCircle className="mr-1 h-3 w-3" />,
        variant: 'destructive',
      },
      cancelled: {
        icon: <XCircle className="mr-1 h-3 w-3" />,
        variant: 'secondary',
      },
    };

    const config = statusConfig[status] || {
      icon: null,
      variant: 'outline' as const,
    };

    return (
      <Badge variant={config.variant} className="flex items-center w-fit">
        {config.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getRequestTypeBadge = (type: string) => {
    const label = type
      .replaceAll('_', ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
    return <Badge variant="secondary">{label}</Badge>;
  };

  const pendingCount = response?.status_counts?.pending ?? 0;
  const approvedCount = response?.status_counts?.approved ?? 0;
  const rejectedCount = response?.status_counts?.rejected ?? 0;

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold">My Change Data</h1>
        <p className="text-muted-foreground mt-1">
          View and manage your submitted change data
        </p>
      </div>

      {/* Info Alert */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>About Change Data</AlertTitle>
        <AlertDescription>
          Changes to sensitive information like banking details and addresses
          require administrator approval before being applied. You&apos;ll be
          notified once your request has been reviewed.
        </AlertDescription>
      </Alert>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingCount}</div>
            <p className="text-xs text-muted-foreground">Awaiting review</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{approvedCount}</div>
            <p className="text-xs text-muted-foreground">Changes applied</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rejected</CardTitle>
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{rejectedCount}</div>
            <p className="text-xs text-muted-foreground">Not approved</p>
          </CardContent>
        </Card>
      </div>

      {/* Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle>Change Request History</CardTitle>
        </CardHeader>
        <CardContent>
          <DataLoader<PortalChangeRequestList>
            isLoading={isLoading}
            error={error}
            data={response}
            emptyStateMessage="No change requests submitted yet"
            showEmptyState={(data) => !data || data.items.length === 0}
          >
            {(response) => (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Investor</TableHead>
                      <TableHead>Requested At</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {response.items.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>
                          <div className="flex flex-col items-start gap-1">
                            {getRequestTypeBadge(request.request_type)}
                            {request.source === 'geena' && (
                              <span className="text-xs text-muted-foreground">
                                from Geena
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {request.investor_name}
                        </TableCell>
                        <TableCell>
                          {formatDate(request.requested_at)}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(request.status)}
                          {request.status === 'rejected' &&
                            request.rejection_reason && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                Reason: {request.rejection_reason}
                              </p>
                            )}
                        </TableCell>
                        <TableCell className="text-right">
                          {request.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCancel(request.id)}
                              disabled={cancellingId === request.id}
                            >
                              {cancellingId === request.id
                                ? 'Cancelling...'
                                : 'Cancel'}
                            </Button>
                          )}
                          {request.status !== 'pending' && (
                            <span className="text-sm text-muted-foreground">
                              {request.status === 'approved' && 'Applied'}
                              {request.status === 'rejected' && 'Declined'}
                              {request.status === 'cancelled' && 'Cancelled'}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {response.total > response.items.length && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Showing {response.items.length} of {response.total} requests
                  </p>
                )}
              </>
            )}
          </DataLoader>
        </CardContent>
      </Card>
    </div>
  );
}
